export type RevenueCatInitStatus =
  | 'idle'
  | 'initializing'
  | 'identifying'
  | 'ready'
  | 'failed'

export interface RevenueCatInitState {
  status: RevenueCatInitStatus
  isConfigured: boolean
  error: Error | null
}

export interface RevenueCatInitLifecycle {
  attemptKey: string
  getState: () => RevenueCatInitState
  isCurrent: () => boolean
  setInitializing: () => void
  setConfigured: () => void
  setIdentifying: () => void
  setReady: () => void
  fail: (error: unknown) => Error
}

export const revenueCatInitIdleState: RevenueCatInitState = {
  status: 'idle',
  isConfigured: false,
  error: null,
}

function toRevenueCatInitError(error: unknown): Error {
  if (error instanceof Error) {
    return error
  }

  if (typeof error === 'string') {
    return new Error(error)
  }

  return new Error('RevenueCat initialization failed')
}

export function createRevenueCatInitController(
  onStateChange?: (state: RevenueCatInitState) => void,
) {
  let state = revenueCatInitIdleState
  let activeAttemptKey: string | null = null
  let inFlightAttemptKey: string | null = null
  let inFlightPromise: Promise<void> | null = null

  const commit = (
    attemptKey: string,
    nextState: RevenueCatInitState,
  ): RevenueCatInitState => {
    if (activeAttemptKey !== attemptKey) {
      return state
    }

    const didChange =
      state.status !== nextState.status ||
      state.isConfigured !== nextState.isConfigured ||
      state.error !== nextState.error

    state = nextState

    if (didChange) {
      onStateChange?.(state)
    }

    return state
  }

  const update = (
    attemptKey: string,
    updater: (currentState: RevenueCatInitState) => RevenueCatInitState,
  ) => commit(attemptKey, updater(state))

  const lifecycleForAttempt = (
    attemptKey: string,
  ): RevenueCatInitLifecycle => ({
    attemptKey,
    getState: () => state,
    isCurrent: () => activeAttemptKey === attemptKey,
    setInitializing: () => {
      update(attemptKey, () => ({
        status: 'initializing',
        isConfigured: false,
        error: null,
      }))
    },
    setConfigured: () => {
      update(attemptKey, () => ({
        status: 'initializing',
        isConfigured: true,
        error: null,
      }))
    },
    setIdentifying: () => {
      update(attemptKey, () => ({
        status: 'identifying',
        isConfigured: true,
        error: null,
      }))
    },
    setReady: () => {
      update(attemptKey, () => ({
        status: 'ready',
        isConfigured: true,
        error: null,
      }))
    },
    fail: (error: unknown) => {
      const normalizedError = toRevenueCatInitError(error)
      update(attemptKey, (currentState) => ({
        status: 'failed',
        isConfigured: currentState.isConfigured,
        error: normalizedError,
      }))
      return normalizedError
    },
  })

  return {
    getState: () => state,
    getActiveAttemptKey: () => activeAttemptKey,
    getInFlightAttemptKey: () => inFlightAttemptKey,
    setIdle: () => {
      activeAttemptKey = null
      inFlightAttemptKey = null
      inFlightPromise = null
      state = revenueCatInitIdleState
      onStateChange?.(state)
    },
    run: (
      attemptKey: string,
      runner: (lifecycle: RevenueCatInitLifecycle) => Promise<void>,
    ) => {
      activeAttemptKey = attemptKey

      if (inFlightPromise != null && inFlightAttemptKey === attemptKey) {
        return inFlightPromise
      }

      const lifecycle = lifecycleForAttempt(attemptKey)

      const runPromise = runner(lifecycle)
        .catch((error) => {
          lifecycle.fail(error)
        })
        .finally(() => {
          if (inFlightAttemptKey === attemptKey) {
            inFlightAttemptKey = null
            inFlightPromise = null
          }
        })

      inFlightAttemptKey = attemptKey
      inFlightPromise = runPromise

      return runPromise
    },
  }
}
